// src/services/ApiClient.ts

import { sessionService, AppTokens } from "./session";
import { coded } from "@/lib/errors";

/**
 * Parse JWT token to extract expiry timestamp
 */
function parseJwtExpiry(token: string): number | undefined {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : undefined; // Convert to milliseconds
  } catch {
    return undefined;
  }
}

class ApiClient {
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  /**
   * A wrapper around the native fetch API that automatically handles JWT authentication and token refreshing.
   * @param endpoint The API endpoint to call (e.g., '/me').
   * @param options The standard RequestInit options for fetch.
   * @returns A Promise resolving to the Response.
   */
  async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const tokens = sessionService.getTokens();
    if (!tokens) {
      // For public endpoints or login, fetch is called directly.
      // For protected endpoints, this check should ideally be done before calling.
      return Promise.reject(coded({ id: "error-signed-out" }));
    }

    // Check if access token is expired and refresh proactively if needed
    if (sessionService.isAccessTokenExpired()) {
      console.log("Access token expired or expiring soon, attempting proactive refresh...");
      try {
        const newAccessToken = await this.handleTokenRefresh();
        if (newAccessToken) {
          // Use the refreshed token
          return await this.makeRequest(endpoint, options, newAccessToken);
        }
      } catch {
        // If proactive refresh fails, clear tokens and reject
        sessionService.clearTokens();
        return Promise.reject(coded({ id: "error-session-expired" }));
      }
    }

    const response = await this.makeRequest(endpoint, options, tokens.access);

    if (response.status === 401) {
      console.warn("Received 401 Unauthorized. Attempting to refresh token...");
      try {
        const newAccessToken = await this.handleTokenRefresh();
        // Retry the original request with the new token
        return await this.makeRequest(endpoint, options, newAccessToken);
      } catch {
        // If refresh fails, log the user out and reject the promise
        sessionService.clearTokens();
        // You might want to redirect the user to a login page here
        return Promise.reject(coded({ id: "error-session-expired" }));
      }
    }

    return response;
  }

  /**
   * Private method to perform the actual fetch call with a given token.
   */
  private async makeRequest(endpoint: string, options: RequestInit, token: string): Promise<Response> {
    const timeoutMs = options.body instanceof FormData ? 120000 : 30000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await fetch(`/api${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });
      return response;
    } catch (error: any) {
      if (error.name === "AbortError") {
        throw coded({ id: "error-timeout" });
      }
      throw coded({ id: "error-network" });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Handles the token refresh flow, ensuring only one refresh request is made at a time.
   */
  private handleTokenRefresh(): Promise<string> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshPromise = this._performTokenRefresh().finally(() => {
        this.isRefreshing = false;
        this.refreshPromise = null;
      });
    }

    return this.refreshPromise.then((token) => {
      if (!token) throw new Error("Failed to acquire new access token.");
      return token;
    });
  }

  /**
   * The core logic for calling the refresh token endpoint.
   */
  private async _performTokenRefresh(): Promise<string | null> {
    const tokens = sessionService.getTokens();
    if (!tokens?.refresh) {
      return null;
    }

    try {
      const response = await fetch("/api/token/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: tokens.refresh }),
      });

      if (!response.ok) {
        throw new Error("Server rejected token refresh request.");
      }

      const newTokens: AppTokens = await response.json();

      // Parse expiry times from refreshed tokens
      const accessExpiry = parseJwtExpiry(newTokens.access);
      const refreshExpiry = parseJwtExpiry(newTokens.refresh);

      sessionService.setTokens({
        ...newTokens,
        accessExpiry,
        refreshExpiry,
      });

      console.log("Token refreshed successfully.");
      return newTokens.access;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // If refresh fails, the session is invalid.
      sessionService.clearTokens();
      return null;
    }
  }
}

// Export a singleton instance of the client for use across the app.
export const apiClient = new ApiClient();
